<template>
    <TheModal class="sm" @close="close">
        <template slot="head">
            {{ $route.meta.title }}
            <a target="_blank" :href="getKBLink('add_recipe')" class="btn btn-link text-nowrap ml-2 px-0"><i class="tlm-icon-question-circle" /></a>
        </template>

        <ValidationObserver id="form-recipe" ref="obs" v-slot="{ invalid }" tag="form" class="form-thin" @submit.prevent="submit">
            <ValidationProvider v-errors.api vid="name" tag="div" class="form-group" rules="required|max:200">
                <input v-model="form.name" v-focus type="text" class="form-control" maxlength="60" placeholder="Введите название">
            </ValidationProvider>

            <ValidationProvider v-if="hasCrt" v-errors.api vid="vat" tag="div" class="form-group" rules="required">
                <select-vat v-model="form.vat" type="text" class="form-control" />
            </ValidationProvider>
        </ValidationObserver>

        <div class="mt-4 mb-3">Выбор ингредиента</div>

        <ValidationObserver ref="componentForm" v-slot="{ invalid }" tag="form" class="form-thin" @submit.prevent="componentAdd">
            <div class="d-flex flex-row flex-wrap">
                <ValidationProvider v-errors.api vid="name" tag="div" rules="required" class="flex-fill form-group">
                    <select-component v-model="componentForm.id" :exclude-ids="excludeIds" class="form-control multiselect-fixed" @select="componentSelected" />
                </ValidationProvider>

                <div class="w-100 d-block d-lg-none" />

                <ValidationProvider v-errors.api vid="volume" tag="div" rules="required|integer|min_value:1|max_value:99999" class="flex-fill form-group form-control-component-volume ml-lg-2">
                    <volume-input ref="componentFormVolume" v-model="componentForm.volume" :disabled="!componentForm.id" :unit="componentForm.unit" class="w-100" />
                </ValidationProvider>

                <div class="align-self-center form-group">
                    <button type="submit" class="btn btn-link text-primary" :disabled="invalid">
                        <i class="tlm-icon-plus" />
                    </button>
                </div>
            </div>
        </ValidationObserver>

        <table class="table table-striped mb-4">
            <thead>
                <tr>
                    <th class="text-nowrap w-100">Ингредиенты</th>
                    <th class="text-nowrap" colspan="2">Кол-во</th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="(component, index) in form.components" :key="index">
                    <td class="truncate">{{ component.name }}</td>
                    <td class="text-nowrap">{{ component.volume }} {{ component.unit | unit }}</td>
                    <td>
                        <button type="button" class="btn btn-icon btn-remove" @click.stop="componentRemove(index)" />
                    </td>
                </tr>
            </tbody>

            <tfoot v-if="form.components.length === 0">
                <tr>
                    <td colspan="42" :class="{ 'text-danger': showMessageEmptyComponents }">Ни одного ингредиента не выбрано.</td>
                </tr>
            </tfoot>
        </table>

        <button type="submit" form="form-recipe" class="btn btn-primary btn-lg-192 mb-4" :disabled="pending">
            {{ form.id ? 'Сохранить' : 'Создать' }}
        </button>
    </TheModal>
</template>

<script>
import store from '@/store'
import SelectVat from '@/components/SelectVat'
import SelectComponent from '@/components/SelectComponent'
import TheModal from '@/components/TheModal'
import VolumeInput from '@/components/VolumeInput'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mixinApiErrors } from '@/validation'

import { toastSuccess } from '@/dialogs'
import cloneDeep from 'lodash/cloneDeep'

import KBLinksService from '@/services/KBLinksService'

const componentForm = {
    id: null,
    name: null,
    volume: null,
    unit: null
}

export default {
    components: {
        SelectComponent,
        SelectVat,
        TheModal,
        ValidationObserver,
        ValidationProvider,
        VolumeInput
    },

    data() {
        return {
            form: {
                id: null,
                name: null,
                vat: null,
                components: []
            },

            componentForm: { ...componentForm },

            pending: false,
            showMessageEmptyComponents: false
        }
    },

    computed: {
        /**
         * Список ID ингредов УЖЕ добавленных в рецепт, они исключаются из выпадающего списка.
         */
        excludeIds() {
            return this.form.components.map(component => component.id)
        },

        hasCrt() {
            return this.$store.getters['company/hasCrt']
        }
    },

    methods: {
        close() {
            this.$router.push({ name: 'stocks.recipes' })
        },

        /**
         * Сохранение / создание рецепта
         */
        async submit() {
            this.showMessageEmptyComponents = false

            if (this.form.components.length === 0) {
                this.showMessageEmptyComponents = true
            }

            const isValid = await this.$refs.obs.validate()

            if (!isValid) {
                return
            }

            this.pending = true

            const done = await this.$store.dispatch('stocks/store', { gtin: null, ...this.form, is_composite: true })

            if (done) {
                this.close()
                toastSuccess(this.form.id ? 'Рецепт сохранен' : 'Рецепт создан')
            } else {
                mixinApiErrors(this.$refs.obs, this.$store.state.stocks.error)
            }

            this.pending = false
        },

        /**
         * Вызывается, когда выбрали какой-либо ингред
         */
        componentSelected(component) {
            this.componentForm = { ...component }

            this.$nextTick(function () {
                this.$refs.componentFormVolume.focus()
            })
        },

        async componentAdd() {
            const isValid = await this.$refs.componentForm.validate()

            if (!isValid) {
                return
            }

            this.form.components.push({ ...this.componentForm })

            this.componentForm = { ...componentForm }

            this.$refs.componentForm.reset()
        },

        componentRemove(index) {
            this.form.components.splice(index, 1)
        },

        getKBLink(key) {
            return KBLinksService.get(key)
        }
    },

    async beforeRouteEnter(to, from, next) {
        if (!to.params.id) {
            return next()
        }

        const recipe = await store.dispatch('stocks/fetchById', parseInt(to.params.id))

        if (recipe) {
            return next(vm => {
                vm.form = cloneDeep(recipe)
            })
        }

        return next({ name: 'stocks.recipes' })
    }
}
</script>
